import React, { Fragment, useMemo } from 'react';

import { cssMap as unboundCssMap } from '@compiled/react';

import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	type ActionItem,
	ActionName,
	Card,
	ElementName,
	FooterBlock,
	MetadataBlock,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	SnippetBlock,
	TitleBlock,
} from '@atlaskit/smart-card';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import { token } from '@atlaskit/tokens';

import { extractUrlHostname } from '../../../utils';
import { logger } from '../../utils/logger';
import { AppIcon } from '../app-icon/app-icon';
import { ErrorBoundary } from '../error-boundary';

import { EMBEDDED_SOURCE_PRODUCTS } from './constants';
import type { KnowledgeCardSource } from './types';

const unboundStyles = unboundCssMap({
	compactContainer: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			width: '100%',
		},
		backgroundColor: token('color.background.neutral.subtle'),
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		minWidth: '220px',
		maxWidth: '360px',
		minHeight: '60px',
		margin: '0 auto',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			textDecoration: 'none',
		},
	},
	fallbackContainer: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	embeddedCardContainer: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			height: '200px',
		},
	},
	richSourceCardContainer: {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'.loader-wrapper': {
			height: '200px',
		},
	},
	titleBlockContainer: {
		fontWeight: token('font.weight.semibold'),
	},
});

function constructSourceUrl({
	source,
	searchSessionId,
}: {
	source: KnowledgeCardSource;
	searchSessionId: string;
}): string {
	if (source.product === 'Confluence' && !!searchSessionId) {
		const url = new URL(source.url);
		url.searchParams.append('search_id', searchSessionId);
		return url.toString();
	}
	return source.url;
}

export enum CardVariant {
	COMPACT = 'COMPACT',
	FULL = 'FULL',
	DROPDOWN_SOURCE = 'DROPDOWN_SOURCE',
}

interface KnowledgeSmartCardProps {
	source: KnowledgeCardSource;
	variant?: CardVariant;
	index?: number;
	searchSessionId?: string;
	onClickHandler?: (params: any) => void;
}

interface SmartCardInternalProps {
	source: KnowledgeCardSource;
	index: number;
	searchSessionId: string;
	onClickHandler: (params: any) => void;
}

const createCardClickHandler =
	(
		source: KnowledgeCardSource,
		index: number,
		searchSessionId: string,
		onClickHandler: (params: any) => void,
		sourceCardType: string,
	) =>
	(e: React.MouseEvent) => {
		onClickHandler?.({
			index,
			source,
			hostname: extractUrlHostname(source.url),
			sourceCardType,
		});
		e.preventDefault();
		window.open(constructSourceUrl({ source, searchSessionId }), '_blank', 'noopener, noreferrer');
	};

const createCardErrorHandler = (componentName: string) => () => {
	logger.error({
		error: new Error(`Error rendering ${componentName}`),
		functionName: componentName,
	});
};

function SimpleFallbackCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	const handleClick = (e: React.MouseEvent) => {
		onClickHandler?.({
			index,
			source,
			hostname: extractUrlHostname(source.url),
			sourceCardType: 'compact',
		});
		e.preventDefault();
		window.open(constructSourceUrl({ source, searchSessionId }), '_blank', 'noopener, noreferrer');
	};

	return (
		<Anchor
			href={source.url}
			xcss={unboundStyles.compactContainer}
			onClick={handleClick}
			testId="knowledge-smart-card-fallback"
		>
			<Box xcss={unboundStyles.fallbackContainer}>
				<Inline space="space.100" alignBlock="center">
					<AppIcon app={source.product} size="large" />
					<Stack space="space.025">
						<Text size="medium" weight="medium" maxLines={1}>
							{source.title || source.url}
						</Text>
						<Text size="small" color="color.text.subtle" maxLines={1}>
							{source.product || ''}
						</Text>
					</Stack>
				</Inline>
			</Box>
		</Anchor>
	);
}

function CompactKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Box xcss={unboundStyles.compactContainer} testId="knowledge-smart-card-compact">
				<CardSSR
					appearance="block"
					url={source.url}
					ui={{
						theme: SmartLinkTheme.Black,
						clickableContainer: true,
						hideBackground: true,
						hideElevation: true,
						size: SmartLinkSize.Medium,
						removeBlockRestriction: true,
					}}
					fallbackComponent={() => (
						<SimpleFallbackCard
							source={source}
							index={index}
							searchSessionId={searchSessionId}
							onClickHandler={onClickHandler}
						/>
					)}
					onClick={createCardClickHandler(
						source,
						index,
						searchSessionId,
						onClickHandler,
						'compact',
					)}
					onError={createCardErrorHandler('CompactKnowledgeSmartCard')}
				>
					<TitleBlock
						maxLines={1}
						size={SmartLinkSize.Small}
						subtitle={[{ name: ElementName.Provider }]}
						position={SmartLinkPosition.Center}
						hideIcon={true}
					/>
				</CardSSR>
			</Box>
		</ErrorBoundary>
	);
}

function StandardKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	const footerActions: ActionItem[] = useMemo(
		() => [
			{ name: ActionName.FollowAction, hideIcon: true },
			{ name: ActionName.PreviewAction, hideIcon: true },
			{ name: ActionName.DownloadAction, hideIcon: true },
		],
		[],
	);

	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Box xcss={unboundStyles.richSourceCardContainer}>
				<Card
					appearance="block"
					url={source.url}
					fallbackComponent={() => (
						<SimpleFallbackCard
							source={source}
							index={index}
							searchSessionId={searchSessionId}
							onClickHandler={onClickHandler}
						/>
					)}
					onClick={createCardClickHandler(
						source,
						index,
						searchSessionId,
						onClickHandler,
						'rich-flexible',
					)}
					onError={createCardErrorHandler('StandardKnowledgeSmartCard')}
				>
					<Box xcss={unboundStyles.titleBlockContainer}>
						<TitleBlock maxLines={1} />
					</Box>
					<MetadataBlock maxLines={1} />
					<SnippetBlock />
					<FooterBlock actions={footerActions} />
				</Card>
			</Box>
		</ErrorBoundary>
	);
}

function EmbeddedKnowledgeSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Box xcss={unboundStyles.embeddedCardContainer}>
				<Card
					appearance="embed"
					ui={{
						clickableContainer: true,
						hideBackground: true,
						hideElevation: true,
						removeBlockRestriction: true,
					}}
					fallbackComponent={() => (
						<SimpleFallbackCard
							source={source}
							index={index}
							searchSessionId={searchSessionId}
							onClickHandler={onClickHandler}
						/>
					)}
					inheritDimensions={true}
					frameStyle="show"
					url={source.url}
					onClick={createCardClickHandler(
						source,
						index,
						searchSessionId,
						onClickHandler,
						'embedded',
					)}
					onError={createCardErrorHandler('EmbeddedKnowledgeSmartCard')}
				/>
			</Box>
		</ErrorBoundary>
	);
}

function DropdownSourceSmartCard({
	source,
	index,
	searchSessionId,
	onClickHandler,
}: SmartCardInternalProps) {
	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Card
				appearance="block"
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				url={source.url}
				onClick={createCardClickHandler(
					source,
					index,
					searchSessionId,
					onClickHandler,
					'dropdown-source',
				)}
				onError={createCardErrorHandler('DropdownSourceSmartCard')}
			>
				<TitleBlock maxLines={1} size={SmartLinkSize.Medium} />
				<SnippetBlock maxLines={2} />
			</Card>
		</ErrorBoundary>
	);
}

export const KnowledgeSmartCard = ({
	source,
	variant,
	index,
	searchSessionId,
	onClickHandler,
}: KnowledgeSmartCardProps) => {
	// Apply defaults for internal components
	const internalProps: SmartCardInternalProps = {
		source,
		index: index || 0,
		searchSessionId: searchSessionId || '',
		onClickHandler: onClickHandler || (() => {}),
	};

	if (variant === CardVariant.COMPACT) {
		return <CompactKnowledgeSmartCard {...internalProps} />;
	}

	if (variant === CardVariant.DROPDOWN_SOURCE) {
		return <DropdownSourceSmartCard {...internalProps} />;
	}

	const shouldUseEmbedded = source.product && source.product in EMBEDDED_SOURCE_PRODUCTS;

	if (shouldUseEmbedded) {
		return <EmbeddedKnowledgeSmartCard {...internalProps} />;
	}

	return <StandardKnowledgeSmartCard {...internalProps} />;
};
